type AvatarMode =
  | 'streaming'
  | 'streaming_only'
  | 'chat_only'
  | 'chat_voice'
  | 'chat_voice_speech';

export interface AvatarConfiguration {
  avatarImgUrl: string;
  azureVoiceId: string;
  speechRecognitionLanguage: string;
  avatarPrompt: string;
  videoInicial: string;
  avatarMode: AvatarMode;
  avatarName: string;
  maxInteracciones?: number | null;
  didAgentId?: string;
  didClientKey?: string;
  didIdleVideoUrl?: string;
}

type ResponseFormat = 'HTML' | 'PlainText' | 'IMG' | 'Markdown';
type ResponseFormatPlus = ResponseFormat | 'FileToShow' | 'Exam'; // formatos de respuesta extra de la web para el manejo del chat

export type AvatarExamQuestionType =
  | 'test'
  | 'multiple_respuesta'
  | 'verdadero_falso'
  | 'desarrollo'
  | 'ordenacion'
  | 'correspondencia'
  | 'rellenar_hueco'
  | 'rellenar_huecos_multiple'
  | 'numerica'
  | 'respuesta_corta'
  | 'opinion_likert'
  | 'oracion_confusa'
  | 'quiz_bowl';

export interface AvatarExamOption {
  id: string;
  texto: string;
}

export interface AvatarExamQuestionData {
  opciones?: AvatarExamOption[];
  origen?: AvatarExamOption[];
  destino?: AvatarExamOption[];
  fragmentos?: AvatarExamOption[];
  texto_con_huecos?: string;
  margen_error_permitido?: number;
  escala?: number[];
  etiquetas?: Record<string, string>;
  pistas?: string[];
}

export interface AvatarExamQuestion {
  id: string;
  tipo: AvatarExamQuestionType;
  enunciado: string;
  puntuacion?: [number, number];
  datos?: AvatarExamQuestionData;
  respuesta?: unknown;
}

export interface AvatarExamPayload {
  examen: {
    titulo?: string;
    descripcion?: string;
    preguntas: AvatarExamQuestion[];
  };
}

/**
 * @property {string} id_proceso - la pregunta no tiene relación con el # contexto # ni con la conversación; "1"  si la pregunta tiene relación con el # CONTEXTO # o con la conversación con el usuario
 * @property {string} respuesta_usuario - mensaje a mostrar al usuario
 * @property {ResponseFormat} formato_respuesta_usuario - valores posibles 'HTML' | 'PlainText' | 'IMG' | 'Markdown'
 * @property {string} archivo_upload - "no" -> por defecto; "yes" -> si necesita en la conversación que el usuario te suba archivos. El Avatar muestra el clip de subir archivo. De momento solo dejamos subir PDFs
 * @property {string} thread_id - valor al que hay que llamar a la función de Diego Curbeira Garay para subir archivos y los meta en un thread
 * @property {string} asignatura_id - valor del id de la asignatura para llamar tb el mismo endpoint de Diego, con el valor de la asignatura
 * @property {string} url_img - URL de la IMG cuando el formato_respuesta_usuario es IMG
 * @property {string} dominio - tema del que se está hablando con el gpt
 * @property {File} file - File to send
 * @property {string} id - internal id to manage messages in chat
 * @property {string} idMessageLinked - internal id about other message to manage it using another message
 * @property {string} time - time when message is received or sent
 */

interface Tag {
  Tag: string;
  frases: string[];
}

interface RespuestaCorta {
  entorno: string;
  tags: Tag[];
  Rol: string;
  acceso_des?: boolean;
}

export interface AvatarResponse {
  id_proceso: string;
  respuesta_usuario: string;
  respuesta_corta: RespuestaCorta;
  formato_respuesta_usuario: ResponseFormatPlus;
  archivo_upload: string;
  thread_id: string;
  asignatura_id: string;
  url_img: string;
  file: File;
  files?: File[]; // Optional array of files for multiple uploads
  id: string;
  idMessageLinked: string;
  time: string;
  dominio: string;
  respuesta_json?: any; // Para dashboards y otros datos JSON complejos
  chart_data?: any; // Para gráficos estructurados enviados por el backend
  fin_conversacion?: 'si' | 'no' | string; // Indica si el asistente da por finalizada la conversación ("si" | "no")
  encuesta_satisfaccion?: { dominio: string }; // El asistente ha dado por terminada la conversación: pedir valoración
  valoracion_enviada?: number; // Nota (1-5) ya enviada para este mensaje
}

export const isConversationEndedResponse = (
  response?: AvatarResponse
): boolean => {
  const rawValue =
    response?.fin_conversacion ??
    (response?.encuesta_satisfaccion as any)?.fin_conversacion;

  if (typeof rawValue === 'boolean') {
    return rawValue;
  }

  if (typeof rawValue !== 'string') {
    return false;
  }

  const normalized = rawValue
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return normalized === 'si' || normalized === 'yes' || normalized === 'true';
};

const isObjectRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const decodeHtmlEntities = (input: string): string =>
  input
    .replace(/&quot;/gi, '"')
    .replace(/&#34;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&nbsp;/gi, ' ');

const normalizeExamCandidate = (value: string): string =>
  decodeHtmlEntities(value)
    .replace(/^\s*<pre[^>]*>/i, '')
    .replace(/<\/pre>\s*$/i, '')
    .trim();

const extractExamCandidates = (value: string): string[] => {
  const candidates = new Set<string>();
  const normalizedValue = normalizeExamCandidate(value);

  if (normalizedValue) {
    candidates.add(normalizedValue);
  }

  const preMatches = value.matchAll(/<pre[^>]*>([\s\S]*?)<\/pre>/gi);
  for (const match of preMatches) {
    const preContent = normalizeExamCandidate(match[1] ?? '');
    if (preContent) {
      candidates.add(preContent);
    }
  }

  const codeMatches = value.matchAll(/<code[^>]*>([\s\S]*?)<\/code>/gi);
  for (const match of codeMatches) {
    const codeContent = normalizeExamCandidate(match[1] ?? '');
    if (codeContent) {
      candidates.add(codeContent);
    }
  }

  const decodedValue = decodeHtmlEntities(value);
  const embeddedObjectMatch = decodedValue.match(/\{[\s\S]*["']examen["'][\s\S]*\}/i);
  if (embeddedObjectMatch?.[0]) {
    candidates.add(normalizeExamCandidate(embeddedObjectMatch[0]));
  }

  return [...candidates];
};

const tryParseExamCandidate = (value: string): AvatarExamPayload | null => {
  try {
    const parsedValue = JSON.parse(value);
    return isAvatarExamPayload(parsedValue) ? parsedValue : null;
  } catch {
    try {
      const normalizedValue = value
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');

      // Permitimos formato tipo diccionario Python/JS con comillas simples.
      // eslint-disable-next-line no-new-func
      const parsedValue = new Function(`return (${normalizedValue});`)();
      return isAvatarExamPayload(parsedValue) ? parsedValue : null;
    } catch {
      return null;
    }
  }
};

export function isAvatarExamPayload(value: unknown): value is AvatarExamPayload {
  if (!isObjectRecord(value) || !isObjectRecord(value.examen)) {
    return false;
  }

  const { preguntas } = value.examen;
  if (!Array.isArray(preguntas) || preguntas.length === 0) {
    return false;
  }

  return preguntas.every((pregunta) => {
    if (!isObjectRecord(pregunta)) {
      return false;
    }

    return (
      typeof pregunta.id === 'string' &&
      typeof pregunta.tipo === 'string' &&
      typeof pregunta.enunciado === 'string'
    );
  });
}

export function parseAvatarExamPayload(
  value: unknown
): AvatarExamPayload | null {
  if (isAvatarExamPayload(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const candidates = extractExamCandidates(value);
  for (const candidate of candidates) {
    const parsedCandidate = tryParseExamCandidate(candidate);
    if (parsedCandidate) {
      return parsedCandidate;
    }
  }

  return null;
}

export function sanitizeAvatarExamPayload(
  payload: AvatarExamPayload
): AvatarExamPayload {
  return {
    examen: {
      ...payload.examen,
      preguntas: payload.examen.preguntas.map(({ respuesta, ...pregunta }) => ({
        ...pregunta,
      })),
    },
  };
}
